import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Upload, X } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

export default function DPApplicationPage() {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const zones = getValues("dp_territory_zones");
  const docTypes = getValues("dp_kyc_doc_types");

  const [form, setForm] = useState({
    legal_name: "", business_name: "", contact_person: "", phone: "", email: "",
    address: "", city: "", state: "", billing_pincode: "", gst_number: "", pan_number: "",
    bank_account_number: "", bank_ifsc: "", bank_name: "",
    territory_zone: "", territory_pincodes: "", territory_brands: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [profileId, setProfileId] = useState(editId || null);
  const [uploads, setUploads] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandError, setBrandError] = useState("");

  useEffect(() => {
    api.get("/dp/brands").then(r => setBrands((r.data || []).filter(b => b.status === "active"))).catch(() => {});
    if (editId) {
      setLoading(true);
      api.get(`/dp/partners/${editId}`)
        .then(r => {
          const d = r.data;
          setForm({
            legal_name: d.legal_name || "", business_name: d.business_name || "",
            contact_person: d.contact_person || "", phone: d.phone || "", email: d.email || "",
            address: d.address || "", city: d.city || "", state: d.state || "",
            gst_number: d.gst_number || "", pan_number: d.pan_number || "",
            bank_account_number: d.bank_account_number || "", bank_ifsc: d.bank_ifsc || "",
            bank_name: d.bank_name || "", territory_zone: d.territory_zone || "",
            billing_pincode: d.billing_pincode || "",
            territory_pincodes: (d.territory_grants || []).map(g => g.pincode).filter((v,i,a) => a.indexOf(v) === i).join(", "),
            territory_brands: (d.territory_grants || []).map(g => g.brand_id).filter((v,i,a) => a.indexOf(v) === i),
          });
          setUploads(d.kyc_documents || []);
        })
        .catch(() => toast.error("Failed to load profile"))
        .finally(() => setLoading(false));
    }
  }, [editId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    setSaving(true);
    try {
      const pincodes = form.territory_pincodes.split(",").map(p => p.trim()).filter(Boolean);
      if (pincodes.length > 0 && form.territory_brands.length === 0) {
        setBrandError("Select at least one brand");
        toast.error("Select at least one brand");
        setSaving(false);
        return;
      }
      setBrandError("");
      const selectedBrands = form.territory_brands;
      const territory_grants = [];
      for (const pin of pincodes) {
        for (const bid of selectedBrands) {
          territory_grants.push({ pincode: pin, brand_id: bid });
        }
      }
      const payload = {
        ...form,
        territory_grants,
      };
      let res;
      if (profileId) {
        res = await api.put(`/dp/partners/${profileId}`, payload);
      } else {
        res = await api.post("/dp/partners", payload);
      }
      if (res.data.duplicate) {
        setDupWarning(res.data.existing_party);
        toast.warning("Possible duplicate found");
        return;
      }
      const id = res.data._id || profileId;
      toast.success(profileId ? "Application updated" : "Application created");
      navigate(`/dp/partner/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleUpload = async (file, docType) => {
    if (!profileId) { toast.error("Save the application first, then upload documents"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await api.post(`/dp/partners/${profileId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      const r = await api.get(`/dp/partners/${profileId}`);
      setUploads(r.data.kyc_documents || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  if (loading) return <div className="p-6 text-xs text-zinc-400">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="dp-application-page">
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <h1 className="text-xl font-semibold text-zinc-900">{profileId ? "Edit" : "New"} DP Application</h1>
      </div>

      {dupWarning && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="dup-warning">
          Possible duplicate: <strong>{dupWarning.name}</strong> ({dupWarning.primary_email || dupWarning.primary_phone}) — already exists in Direct Partners.
        </div>
      )}

      <div className="space-y-5">
        <Section title="Business Details">
          <Field label="Business Name *" value={form.business_name} onChange={v => set("business_name", v)} testid="input-business-name" />
          <Field label="Legal Name" value={form.legal_name} onChange={v => set("legal_name", v)} testid="input-legal-name" />
          <Field label="Contact Person" value={form.contact_person} onChange={v => set("contact_person", v)} testid="input-contact-person" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone" value={form.phone} onChange={v => set("phone", v)} testid="input-phone" />
            <Field label="Email" value={form.email} onChange={v => set("email", v)} testid="input-email" />
          </div>
          <Field label="Address" value={form.address} onChange={v => set("address", v)} testid="input-address" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="City" value={form.city} onChange={v => set("city", v)} testid="input-city" />
            <Field label="State" value={form.state} onChange={v => set("state", v)} testid="input-state" />
          </div>
          <Field label="Billing Pincode (6 digits)" value={form.billing_pincode} onChange={v => set("billing_pincode", v)} testid="input-billing-pincode" />
        </Section>

        <Section title="Tax & Bank">
          <div className="grid grid-cols-2 gap-3">
            <Field label="GST Number" value={form.gst_number} onChange={v => set("gst_number", v)} testid="input-gst" />
            <Field label="PAN Number" value={form.pan_number} onChange={v => set("pan_number", v)} testid="input-pan" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Bank Account No." value={form.bank_account_number} onChange={v => set("bank_account_number", v)} testid="input-bank-account" />
            <Field label="IFSC" value={form.bank_ifsc} onChange={v => set("bank_ifsc", v)} testid="input-ifsc" />
            <Field label="Bank Name" value={form.bank_name} onChange={v => set("bank_name", v)} testid="input-bank-name" />
          </div>
        </Section>

        <Section title="Territory">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-500">Zone</Label>
              <Select value={form.territory_zone} onValueChange={v => set("territory_zone", v)}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-zone"><SelectValue placeholder="Select zone" /></SelectTrigger>
                <SelectContent>{zones.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Pincodes (comma-separated)" value={form.territory_pincodes} onChange={v => set("territory_pincodes", v)} testid="input-pincodes" />
          </div>
          {brands.length > 0 && (
            <div>
              <Label className="text-xs text-zinc-500">Brands (select for these pincodes)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {brands.map(b => (
                  <label key={b._id} className="flex items-center gap-1 text-xs text-zinc-600">
                    <input type="checkbox" checked={form.territory_brands.includes(b._id)} onChange={e => {
                      if (e.target.checked) set("territory_brands", [...form.territory_brands, b._id]);
                      else set("territory_brands", form.territory_brands.filter(x => x !== b._id));
                    }} className="rounded border-zinc-300" data-testid={`brand-check-${b._id}`} />
                    {b.name}
                  </label>
                ))}
              </div>
              {brandError ? (
                <p className="text-[11px] text-red-600 mt-1" data-testid="brand-required-error">{brandError}</p>
              ) : (
                <p className="text-[10px] text-zinc-400 mt-1">Select at least one brand for the pincodes entered</p>
              )}
            </div>
          )}
        </Section>

        <Section title="Deposit">
          <p className="text-xs text-zinc-400 italic">Security deposits are not required. Advances are handled via existing OMS after approval.</p>
        </Section>

        {profileId && (
          <Section title="KYC Documents">
            {uploads.length > 0 && (
              <div className="mb-3 space-y-1">
                {uploads.map(d => (
                  <div key={d.doc_id} className="flex items-center justify-between text-xs border border-zinc-100 rounded-sm px-2 py-1.5">
                    <span className="text-zinc-700 capitalize">{d.doc_type?.replace(/_/g, " ")} — {d.filename}</span>
                    <span className="text-zinc-400">{fmtDate(d.uploaded_at)}</span>
                  </div>
                ))}
              </div>
            )}
            <DocUpload docTypes={docTypes} onUpload={handleUpload} />
          </Section>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="submit-application">
            {saving ? "Saving..." : profileId ? "Update Application" : "Submit Application"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4">
      <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs text-zinc-500">{label}</Label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className="h-8 text-xs mt-1" data-testid={testid} />
    </div>
  );
}

function DocUpload({ docTypes, onUpload }) {
  const [selectedType, setSelectedType] = useState("");
  const [file, setFile] = useState(null);
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Label className="text-xs text-zinc-500">Document Type</Label>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-doc-type"><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>{docTypes.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex-1">
        <Label className="text-xs text-zinc-500">File</Label>
        <Input type="file" className="h-8 text-xs mt-1" onChange={e => setFile(e.target.files[0])} data-testid="input-file" />
      </div>
      <Button size="sm" variant="outline" disabled={!file || !selectedType} onClick={() => { onUpload(file, selectedType); setFile(null); }} data-testid="upload-doc-btn">
        <Upload className="w-3.5 h-3.5 mr-1" /> Upload
      </Button>
    </div>
  );
}
